import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Spinner,
  Alert,
  Badge,
  Modal,
  DropdownButton,
  Dropdown,
  ListGroup,
  InputGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  FaArrowLeft,
  FaBed,
  FaFileExcel,
  FaSync,
  FaUserPlus,
  FaSignInAlt,
  FaSignOutAlt,
  FaTrash,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import pythonApiClient from "../../../services/api/pythonBaseApi";
import { zonaApi } from "../../../services/api/zonaApi";
import { moduloApi } from "../../../services/api/moduloApi";
import "react-toastify/dist/ReactToastify.css";

const norm = (s) => (s || "").toString().trim().toLowerCase();

const badgeVariantForEstado = (nombre) => {
  const n = norm(nombre);
  if (n.includes("libre")) return "success";
  if (n.includes("reserv")) return "warning";
  if (n.includes("ocup")) return "danger";
  return "secondary";
};

/** Muestra DNI / nombre solo cuando la cama está reservada u ocupada (según nombre del estado). */
const esCamaReservadaUOcupada = (estadoNombre) => {
  const n = norm(estadoNombre);
  return n.includes("reserv") || n.includes("ocup");
};

const esCamaLibre = (estadoNombre) => norm(estadoNombre).includes("libre");

/** Estado de cama “reservado” (pendiente de check-in), sin ser ocupada. */
const esCamaSoloReservada = (estadoNombre) => {
  const n = norm(estadoNombre);
  return n.includes("reserv") && !n.includes("ocup");
};

const esCamaOcupada = (estadoNombre) => norm(estadoNombre).includes("ocup");

const msgApi = (e) =>
  e?.response?.data?.detail ||
  e?.response?.data?.message ||
  e?.message ||
  "Error en la operación";

const fechaLocalYmd = () => {
  const hoy = new Date();
  const y = hoy.getFullYear();
  const m = String(hoy.getMonth() + 1).padStart(2, "0");
  const d = String(hoy.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Fecha/hora de BD o ISO para mostrar en PE */
const fmtFechaHoraPe = (v) => {
  if (v == null || v === "") return null;
  const s = String(v).trim();
  if (!s) return null;
  const d =
    s.includes("T") && !s.includes(" ")
      ? new Date(s)
      : new Date(s.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("es-PE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const textoExportFechasAsignacion = (c) => {
  const parts = [];
  const fr = fmtFechaHoraPe(c.fecha_reserva);
  const fci = fmtFechaHoraPe(c.fecha_check_in);
  if (fr) parts.push(`Reserva: ${fr}`);
  if (fci) parts.push(`Check-in: ${fci}`);
  return parts.join(" | ");
};

const CeldaFechasReservaCheckin = ({ c }) => {
  const fr = fmtFechaHoraPe(c.fecha_reserva);
  const fci = fmtFechaHoraPe(c.fecha_check_in);
  if (!fr && !fci) return "—";
  return (
    <div className="small text-start">
      {fr && <div className="text-nowrap">Reserva: {fr}</div>}
      {fci && <div className="text-nowrap">Check-in: {fci}</div>}
    </div>
  );
};

const EstadoCamasProyecto = () => {
  const navigate = useNavigate();
  const selectedProject = JSON.parse(
    localStorage.getItem("selectedProject") || "null"
  );

  const [estadosCatalogo, setEstadosCatalogo] = useState([]);
  const [selectedEstadoIds, setSelectedEstadoIds] = useState([]);
  const [camas, setCamas] = useState([]);
  const [loadingCatalogo, setLoadingCatalogo] = useState(false);
  const [loadingCamas, setLoadingCamas] = useState(false);
  const [exporting, setExporting] = useState(false);

  /** null = todas las zonas / todos los módulos */
  const [filterZonaId, setFilterZonaId] = useState(null);
  const [filterModuloId, setFilterModuloId] = useState(null);
  const [zonasList, setZonasList] = useState([]);
  const [modulosOpciones, setModulosOpciones] = useState([]);
  const [loadingZonas, setLoadingZonas] = useState(false);
  const [loadingModulosOpciones, setLoadingModulosOpciones] = useState(false);

  const [filterTrabajadorDocumento, setFilterTrabajadorDocumento] = useState("");
  const [filterTrabajadorNombre, setFilterTrabajadorNombre] = useState("");
  const filterTrabajadorDocumentoRef = useRef("");
  const filterTrabajadorNombreRef = useRef("");

  const [camaParaReserva, setCamaParaReserva] = useState(null);
  /** null | 'reserva' | 'checkin_directo' — solo cuando la cama está libre */
  const [modalLibreTipo, setModalLibreTipo] = useState(null);
  const [fechaCheckinDirecto, setFechaCheckinDirecto] = useState("");
  const [busquedaTrabajador, setBusquedaTrabajador] = useState("");
  const [hitsTrabajador, setHitsTrabajador] = useState([]);
  const [trabajadorElegido, setTrabajadorElegido] = useState(null);
  const [buscandoTrabajador, setBuscandoTrabajador] = useState(false);
  const [guardandoReserva, setGuardandoReserva] = useState(false);

  /** @type {null | { kind: string, row: object, title?: string, body?: string }} */
  const [confirmAccion, setConfirmAccion] = useState(null);
  const [rowBusyId, setRowBusyId] = useState(null);

  useEffect(() => {
    document.title = "Estado de camas (proyecto) — SGC";
  }, []);

  useEffect(() => {
    if (!camaParaReserva) {
      setHitsTrabajador([]);
      return;
    }
    const q = busquedaTrabajador.trim();
    if (q.length < 2) {
      setHitsTrabajador([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setBuscandoTrabajador(true);
      try {
        const res = await pythonApiClient.get(
          "/procesos/hoteleria/reserva-manual-camas/buscar-trabajadores",
          { params: { busqueda: q } }
        );
        if (!cancelled) {
          setHitsTrabajador(Array.isArray(res.data?.data) ? res.data.data : []);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          toast.error(msgApi(e));
          setHitsTrabajador([]);
        }
      } finally {
        if (!cancelled) setBuscandoTrabajador(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [busquedaTrabajador, camaParaReserva]);

  const fetchZonasProyecto = useCallback(async () => {
    if (!selectedProject?.id) return [];
    setLoadingZonas(true);
    try {
      const res = await zonaApi.getZonas(selectedProject.id);
      const arr = Array.isArray(res.data) ? res.data : [];
      setZonasList(arr);
      return arr;
    } catch (e) {
      console.error(e);
      toast.error("No se pudieron cargar las zonas del proyecto");
      setZonasList([]);
      return [];
    } finally {
      setLoadingZonas(false);
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    if (!selectedProject?.id) return;
    setFilterZonaId(null);
    setFilterModuloId(null);
    setFilterTrabajadorDocumento("");
    setFilterTrabajadorNombre("");
    filterTrabajadorDocumentoRef.current = "";
    filterTrabajadorNombreRef.current = "";
    fetchZonasProyecto();
  }, [selectedProject?.id, fetchZonasProyecto]);

  useEffect(() => {
    if (!selectedProject?.id) return;
    let cancelled = false;
    (async () => {
      setLoadingModulosOpciones(true);
      try {
        if (filterZonaId != null) {
          const res = await moduloApi.getModulosByZona(filterZonaId);
          if (cancelled) return;
          setModulosOpciones(Array.isArray(res.data) ? res.data : []);
          return;
        }
        if (zonasList.length === 0) {
          if (!cancelled) setModulosOpciones([]);
          return;
        }
        const seen = new Map();
        await Promise.all(
          zonasList.map(async (z) => {
            const res = await moduloApi.getModulosByZona(z.id);
            const list = Array.isArray(res.data) ? res.data : [];
            list.forEach((m) => {
              if (!seen.has(m.id)) seen.set(m.id, m);
            });
          })
        );
        if (cancelled) return;
        const merged = Array.from(seen.values()).sort((a, b) => {
          const za = String(a.nombre_zona || "");
          const zb = String(b.nombre_zona || "");
          if (za !== zb) return za.localeCompare(zb);
          return String(a.nombre).localeCompare(String(b.nombre));
        });
        setModulosOpciones(merged);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          toast.error("No se pudieron cargar los módulos");
          setModulosOpciones([]);
        }
      } finally {
        if (!cancelled) setLoadingModulosOpciones(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProject?.id, filterZonaId, zonasList]);

  const loadCamasWithIds = useCallback(
    async (ids) => {
      if (!selectedProject?.id) return;
      setLoadingCamas(true);
      try {
        const params = new URLSearchParams();
        params.set("proyecto_id", String(selectedProject.id));
        if (filterZonaId != null) {
          params.set("zona_id", String(filterZonaId));
        }
        if (filterModuloId != null) {
          params.set("modulo_id", String(filterModuloId));
        }
        (ids || []).forEach((id) =>
          params.append("estado_cama_ids", String(id))
        );
        const doc = (filterTrabajadorDocumentoRef.current || "").trim();
        const nom = (filterTrabajadorNombreRef.current || "").trim();
        if (doc) {
          params.set("trabajador_documento", doc);
        }
        if (nom) {
          params.set("trabajador_nombre", nom);
        }
        const res = await pythonApiClient.get(
          `/reportes/hoteleria/camas/?${params.toString()}`,
          { headers: { "X-Proyecto-ID": String(selectedProject.id) } }
        );
        if (res.data?.success) {
          setCamas(res.data.camas || []);
          if ((res.data.camas || []).length === 0) {
            toast.info("No hay camas con los filtros seleccionados");
          }
        } else {
          toast.error(res.data?.message || "Error al cargar camas");
          setCamas([]);
        }
      } catch (e) {
        console.error(e);
        toast.error(
          e.response?.data?.detail || "Error al consultar el estado de las camas"
        );
        setCamas([]);
      } finally {
        setLoadingCamas(false);
      }
    },
    [selectedProject?.id, filterZonaId, filterModuloId]
  );

  const loadCatalogo = useCallback(async () => {
    setLoadingCatalogo(true);
    try {
      const res = await pythonApiClient.get(
        "/reportes/hoteleria/camas/estados-catalogo"
      );
      const list = res.data?.data || [];
      setEstadosCatalogo(list);
      const prefer = list
        .filter((e) => {
          const n = norm(e.nombre);
          return (
            n.includes("libre") ||
            n.includes("reserv") ||
            n.includes("ocup")
          );
        })
        .map((e) => e.id);
      const initialIds =
        prefer.length > 0 ? prefer : list.map((e) => e.id);
      setSelectedEstadoIds(initialIds);
      return initialIds;
    } catch (e) {
      console.error(e);
      toast.error("No se pudo cargar el catálogo de estados de cama");
      setEstadosCatalogo([]);
      return [];
    } finally {
      setLoadingCatalogo(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedProject?.id) return;
    let cancelled = false;
    (async () => {
      const ids = await loadCatalogo();
      if (cancelled) return;
      await loadCamasWithIds(ids);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProject?.id, loadCatalogo, loadCamasWithIds]);

  const loadCamas = useCallback(() => {
    return loadCamasWithIds(selectedEstadoIds);
  }, [loadCamasWithIds, selectedEstadoIds]);

  const abrirModalReserva = (row) => {
    setModalLibreTipo("reserva");
    setFechaCheckinDirecto("");
    setCamaParaReserva(row);
    setBusquedaTrabajador("");
    setHitsTrabajador([]);
    setTrabajadorElegido(null);
  };

  const abrirModalCheckinDirecto = (row) => {
    setModalLibreTipo("checkin_directo");
    setFechaCheckinDirecto(fechaLocalYmd());
    setCamaParaReserva(row);
    setBusquedaTrabajador("");
    setHitsTrabajador([]);
    setTrabajadorElegido(null);
  };

  const cerrarModalReserva = () => {
    setCamaParaReserva(null);
    setModalLibreTipo(null);
    setFechaCheckinDirecto("");
    setBusquedaTrabajador("");
    setHitsTrabajador([]);
    setTrabajadorElegido(null);
  };

  const confirmarModalLibreCama = async () => {
    if (!camaParaReserva || !trabajadorElegido || !selectedProject?.id) return;
    if (trabajadorElegido?.tiene_reserva) return;
    setGuardandoReserva(true);
    try {
      if (modalLibreTipo === "checkin_directo") {
        const fd = new FormData();
        fd.append(
          "dni",
          String(trabajadorElegido.numero_documento_identidad || "").trim()
        );
        fd.append("fecha_checkin", fechaCheckinDirecto || fechaLocalYmd());
        fd.append("zona", String(camaParaReserva.zona || ""));
        fd.append("modulo", String(camaParaReserva.modulo || ""));
        fd.append("habitacion", String(camaParaReserva.habitacion ?? ""));
        fd.append("cama", String(camaParaReserva.cama ?? ""));
        fd.append("proyecto_id", String(selectedProject.id));
        fd.append("auto_complete_dni_length", "false");
        const res = await pythonApiClient.post(
          "/procesos/hoteleria/check-in-manual/individual",
          fd
        );
        if (res.data?.success) {
          toast.success(res.data.message || "Check-in realizado");
          cerrarModalReserva();
          await loadCamas();
        } else {
          toast.error(res.data?.message || "No se pudo hacer check-in");
        }
        return;
      }
      const fd = new FormData();
      fd.append("trabajador_id", String(trabajadorElegido.id));
      fd.append("zona_nombre", String(camaParaReserva.zona || ""));
      fd.append("modulo_nombre", String(camaParaReserva.modulo || ""));
      fd.append("habitacion_numero", String(camaParaReserva.habitacion ?? ""));
      fd.append("cama_codigo", String(camaParaReserva.cama ?? ""));
      const res = await pythonApiClient.post(
        "/procesos/hoteleria/reserva-manual-camas/reserva-individual",
        fd
      );
      if (res.data?.success) {
        toast.success(res.data.message || "Reserva creada");
        cerrarModalReserva();
        await loadCamas();
      } else {
        toast.error(res.data?.message || "No se pudo reservar");
      }
    } catch (e) {
      toast.error(msgApi(e));
    } finally {
      setGuardandoReserva(false);
    }
  };

  const ejecutarConfirmacion = async () => {
    if (!confirmAccion?.row || !selectedProject?.id) return;
    const { kind, row } = confirmAccion;
    const dni = String(row.trabajador_dni || "").trim();
    const pid = selectedProject.id;
    setConfirmAccion(null);
    setRowBusyId(row.id);
    try {
      if (kind === "checkin") {
        const r = await pythonApiClient.post("/procesos/hoteleria/check-in/", {
          dni,
        });
        if (r.data?.success) {
          toast.success(r.data.message || "Check-in realizado");
          await loadCamas();
        } else {
          toast.error(r.data?.message || "Check-in no completado");
        }
        return;
      }
      if (kind === "checkout") {
        const r = await pythonApiClient.post("/procesos/hoteleria/check-out/", {
          dni,
          auto_pad_dni_8: false,
        });
        if (r.data?.success) {
          toast.success(r.data.message || "Check-out realizado");
          await loadCamas();
        } else {
          toast.error(r.data?.message || "Check-out no completado");
        }
        return;
      }
      if (kind === "quitar_reserva") {
        await pythonApiClient.post(
          "/procesos/hoteleria/eliminar-reservas/eliminar-individual",
          { dni }
        );
        toast.success("Reserva anulada");
        await loadCamas();
        return;
      }
      if (kind === "anular_checkin_solo") {
        const aid = row.asignacion_id;
        if (!aid) {
          toast.error(
            "Falta ID de asignación en el listado. Actualice datos o use otro proceso."
          );
          return;
        }
        const r = await pythonApiClient.post(
          "/procesos/hoteleria/eliminar-check-in/eliminar-por-ids",
          {
            proyecto_id: pid,
            asignacion_ids: [Number(aid)],
            modo: "solo_checkin",
          }
        );
        if (r.data?.success) {
          toast.success(
            r.data.message || "Check-in anulado; la cama vuelve a reservada."
          );
          await loadCamas();
        } else {
          toast.error(r.data?.message || "No se pudo anular el check-in");
        }
        return;
      }
      if (kind === "anular_checkin_liberar") {
        const aid = row.asignacion_id;
        if (aid) {
          const r = await pythonApiClient.post(
            "/procesos/hoteleria/eliminar-check-in/eliminar-por-ids",
            {
              proyecto_id: pid,
              asignacion_ids: [Number(aid)],
              modo: "checkin_y_reserva",
            }
          );
          if (r.data?.success) {
            toast.success(
              r.data.message || "Asignación cancelada y cama liberada."
            );
            await loadCamas();
          } else {
            toast.error(r.data?.message || "No se pudo completar");
          }
        } else {
          const r = await pythonApiClient.post(
            "/procesos/hoteleria/eliminar-check-in/eliminar-individual",
            { dni, proyecto_id: pid }
          );
          if (r.data?.success) {
            toast.success(r.data.message || "Check-in eliminado");
            await loadCamas();
          } else {
            toast.error(r.data?.message || "No se pudo eliminar");
          }
        }
      }
    } catch (e) {
      toast.error(msgApi(e));
    } finally {
      setRowBusyId(null);
    }
  };

  const toggleEstado = (id) => {
    setSelectedEstadoIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const seleccionarTodosEstados = () => {
    setSelectedEstadoIds(estadosCatalogo.map((e) => e.id));
  };

  const limpiarEstados = () => {
    setSelectedEstadoIds([]);
  };

  const exportarExcel = () => {
    if (!camas.length) {
      toast.warning("No hay datos para exportar");
      return;
    }
    setExporting(true);
    try {
      const rows = camas.map((c) => {
        const showTrab = esCamaReservadaUOcupada(c.estado);
        return {
          ID: c.id,
          Zona: c.zona,
          Módulo: c.modulo,
          Habitación: c.habitacion,
          "Reserva / Check-in": textoExportFechasAsignacion(c),
          Cama: c.cama,
          "Estado cama": c.estado,
          "DNI trabajador": showTrab ? (c.trabajador_dni ?? "") : "",
          "Nombre y apellidos": showTrab ? (c.trabajador_nombre_apellidos ?? "") : "",
        };
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Camas");
      const fecha = new Date().toISOString().split("T")[0];
      const safeName = (selectedProject?.nombre || "proyecto")
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "_")
        .slice(0, 40);
      XLSX.writeFile(wb, `Estado_camas_${safeName}_${fecha}.xlsx`);
      toast.success(`Exportado: ${rows.length} camas`);
    } catch (e) {
      console.error(e);
      toast.error("Error al generar Excel");
    } finally {
      setExporting(false);
    }
  };

  const harmonyStyles = `
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title {
            font-size: 2.2rem;
            font-weight: 700;
            margin: 0;
          }
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          .dh-card {
            background: white;
            border-radius: 20px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
            border: none;
            overflow: hidden;
            transition: all 0.3s ease;
          }
          .dh-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
          }
          .dh-card-header {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-bottom: 2px solid #dee2e6;
            padding: 1.5rem;
            font-weight: 600;
            font-size: 1.2rem;
            color: #495057;
          }
          .dh-card-body {
            padding: 2rem;
          }
          .dh-card-body-flush {
            padding: 0 !important;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            border-radius: 12px;
            padding: 0.75rem 1.5rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
          }
          .btn-dh-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
            color: white;
          }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
            border: none;
            border-radius: 12px;
            padding: 0.75rem 1.5rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
          }
          .btn-dh-secondary:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(108, 117, 125, 0.4);
            color: white;
          }
          .btn-dh-success {
            background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
            border: none;
            border-radius: 12px;
            padding: 0.75rem 1.5rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
          }
          .btn-dh-success:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4);
            color: white;
          }
          .btn-dh-primary:disabled,
          .btn-dh-secondary:disabled,
          .btn-dh-success:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }
          .dh-table-wrap {
            max-height: 70vh;
            overflow: auto;
            border-radius: 0 0 15px 15px;
          }
          .dh-table {
            background: white;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            margin-bottom: 0;
          }
          .dh-table thead th {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1rem;
            font-weight: 600;
            font-size: 0.95rem;
          }
          .dh-table tbody td {
            padding: 1rem;
            border-bottom: 1px solid #f1f3f4;
            vertical-align: middle;
          }
          .dh-table tbody tr:hover {
            background: #f8f9fa;
          }
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .dh-fade-in {
            animation: fadeInUp 0.6s ease-out;
          }
          @media (max-width: 768px) {
            .dh-main-title { font-size: 1.8rem; }
            .dh-card-body { padding: 1.5rem; }
          }
          .dh-select-ubicacion {
            border: 2px solid #e9ecef;
            border-radius: 12px;
            padding: 0.6rem 0.75rem;
            background: #f8f9fa;
          }
          .dh-select-ubicacion:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.2);
            background: #fff;
          }
        `;

  if (!selectedProject?.id) {
    return (
      <>
        <style>{harmonyStyles}</style>
        <div className="dh-maintenance-bg py-5">
          <ToastContainer />
          <Container>
            <Alert variant="warning" className="rounded-4 shadow-sm border-0">
              Seleccione un proyecto en la barra superior para ver el estado de
              las camas.
            </Alert>
            <Button className="btn-dh-secondary" onClick={() => navigate("/home")}>
              <FaArrowLeft className="me-2" />
              Volver
            </Button>
          </Container>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Digital Harmony — mismo lenguaje visual que MantenimientoCamas / Hotelería */}
      <style>{harmonyStyles}</style>
      <div className="dh-maintenance-bg">
        <ToastContainer />
        <div className="dh-main-header">
          <Container fluid>
            <Row className="align-items-center">
              <Col lg={8}>
                <h1 className="dh-main-title d-flex align-items-center gap-3 flex-wrap">
                  <FaBed className="opacity-90" aria-hidden />
                  Estado de camas del proyecto
                </h1>
                <p className="dh-main-subtitle mb-0">
                  Proyecto: <strong>{selectedProject.nombre}</strong> — Filtre por
                  zona, módulo y estados de cama; use &quot;Todas&quot; / &quot;Todos&quot;
                  para no acotar. Cama libre: puede <strong>reservar</strong> o hacer{" "}
                  <strong>check-in directo</strong> (sin paso intermedio de reserva). El resto
                  de acciones depende del estado de la cama.
                </p>
              </Col>
              <Col
                lg={4}
                className="d-flex gap-2 flex-wrap justify-content-lg-end mt-3 mt-lg-0"
              >
                <Button
                  className="btn-dh-secondary"
                  onClick={() => navigate("/home")}
                >
                  <FaArrowLeft className="me-2" />
                  Volver
                </Button>
                <Button
                  className="btn-dh-primary"
                  onClick={async () => {
                    await fetchZonasProyecto();
                    const ids = await loadCatalogo();
                    await loadCamasWithIds(ids);
                  }}
                  disabled={
                    loadingCamas ||
                    loadingCatalogo ||
                    loadingZonas ||
                    loadingModulosOpciones
                  }
                >
                  <FaSync className="me-2" />
                  Actualizar
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <Container fluid className="pb-5">
          <Row className="dh-fade-in g-4 mb-4 align-items-stretch">
            <Col lg={5} md={12}>
              <Card className="dh-card h-100">
                <Card.Header className="dh-card-header">
                  Estados de cama (selección múltiple)
                </Card.Header>
                <Card.Body className="dh-card-body">
                  {loadingCatalogo ? (
                    <div className="text-center py-3 text-secondary">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Cargando…
                    </div>
                  ) : estadosCatalogo.length === 0 ? (
                    <Alert variant="light" className="mb-0 small rounded-3">
                      No hay estados configurados.
                    </Alert>
                  ) : (
                    <>
                      <div className="d-flex gap-2 mb-3 flex-wrap">
                        <Button
                          size="sm"
                          className="btn-dh-primary"
                          style={{ padding: "0.45rem 1rem" }}
                          onClick={seleccionarTodosEstados}
                        >
                          Todos
                        </Button>
                        <Button
                          size="sm"
                          className="btn-dh-secondary"
                          style={{ padding: "0.45rem 1rem" }}
                          onClick={limpiarEstados}
                        >
                          Ninguno
                        </Button>
                        <Button
                          size="sm"
                          className="btn-dh-success"
                          style={{ padding: "0.45rem 1rem" }}
                          onClick={loadCamas}
                          disabled={loadingCamas}
                        >
                          Aplicar filtro
                        </Button>
                      </div>
                      <Form className="estados-checkboxes">
                        {estadosCatalogo.map((e) => (
                          <Form.Check
                            key={e.id}
                            type="checkbox"
                            id={`estado-cama-${e.id}`}
                            label={e.nombre}
                            checked={selectedEstadoIds.includes(e.id)}
                            onChange={() => toggleEstado(e.id)}
                            className="mb-2"
                          />
                        ))}
                      </Form>
                      <p className="text-muted small mb-0 mt-3">
                        Si no marca ninguno y pulsa &quot;Aplicar filtro&quot;, se
                        listan <strong>todas</strong> las camas del proyecto (con
                        zona, módulo y trabajador si los indicó a la derecha).
                      </p>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={12}>
              <Card className="dh-card h-100">
                <Card.Header className="dh-card-header">
                  Trabajador
                </Card.Header>
                <Card.Body className="dh-card-body">
                  <Form.Group className="mb-3" controlId="filtro-nombre-trabajador">
                    <Form.Label className="small text-muted fw-semibold">
                      Nombres y apellidos
                    </Form.Label>
                    <Form.Control
                      className="dh-select-ubicacion"
                      type="text"
                      placeholder="Nombre completo (parcial)"
                      value={filterTrabajadorNombre}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFilterTrabajadorNombre(v);
                        filterTrabajadorNombreRef.current = v;
                      }}
                      autoComplete="off"
                    />
                  </Form.Group>
                  <Form.Group className="mb-0" controlId="filtro-doc-trabajador">
                    <Form.Label className="small text-muted fw-semibold">
                      Documento
                    </Form.Label>
                    <Form.Control
                      className="dh-select-ubicacion"
                      type="text"
                      placeholder="Ej. 12345678"
                      value={filterTrabajadorDocumento}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFilterTrabajadorDocumento(v);
                        filterTrabajadorDocumentoRef.current = v;
                      }}
                      autoComplete="off"
                    />
                  </Form.Group>
                  <p className="text-muted small mb-0 mt-3">
                    Coincidencia parcial; solo camas con asignación vigente. Pulse
                    &quot;Aplicar filtro&quot; en la primera tarjeta.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4} md={12}>
              <Card className="dh-card h-100">
                <Card.Header className="dh-card-header">
                  Zona y módulo
                </Card.Header>
                <Card.Body className="dh-card-body">
                  {loadingZonas ? (
                    <div className="text-center py-2 text-secondary small">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Cargando zonas…
                    </div>
                  ) : (
                    <>
                      <Form.Group className="mb-3" controlId="filtro-zona">
                        <Form.Label className="small text-muted fw-semibold">
                          Zona
                        </Form.Label>
                        <Form.Select
                          className="dh-select-ubicacion"
                          value={filterZonaId ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setFilterZonaId(v === "" ? null : Number(v));
                            setFilterModuloId(null);
                          }}
                          disabled={loadingModulosOpciones}
                        >
                          <option value="">Todas las zonas</option>
                          {zonasList.map((z) => (
                            <option key={z.id} value={z.id}>
                              {z.nombre}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-0" controlId="filtro-modulo">
                        <Form.Label className="small text-muted fw-semibold">
                          Módulo
                        </Form.Label>
                        <Form.Select
                          className="dh-select-ubicacion"
                          value={filterModuloId ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setFilterModuloId(v === "" ? null : Number(v));
                          }}
                          disabled={
                            loadingModulosOpciones || zonasList.length === 0
                          }
                        >
                          <option value="">Todos los módulos</option>
                          {modulosOpciones.map((m) => (
                            <option key={m.id} value={m.id}>
                              {filterZonaId == null && m.nombre_zona
                                ? `[${m.nombre_zona}] ${m.nombre} — ${m.codigo}`
                                : `${m.nombre} — ${m.codigo}`}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      {loadingModulosOpciones && (
                        <div className="text-secondary small mt-2">
                          <Spinner animation="border" size="sm" className="me-1" />
                          Actualizando módulos…
                        </div>
                      )}
                      <p className="text-muted small mb-0 mt-3">
                        La ubicación se aplica al pulsar &quot;Aplicar filtro&quot; en
                        la tarjeta de estados de cama.
                      </p>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="dh-fade-in mb-3">
            <Col xs={12}>
              <p className="text-muted small mb-2">
                Un <strong>check-out</strong> deja la cama libre; no hay acción en pantalla para
                revertir un check-out ya cerrado (requiere soporte o datos históricos).
              </p>
            </Col>
            <Col
              xs={12}
              className="d-flex justify-content-end align-items-center flex-wrap gap-2"
            >
              <Button
                className="btn-dh-success"
                onClick={exportarExcel}
                disabled={exporting || !camas.length}
              >
                <FaFileExcel className="me-2" />
                Exportar Excel
              </Button>
            </Col>
          </Row>

          <Row className="dh-fade-in">
            <Col xs={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>Grilla de camas</span>
                  {loadingCamas ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <Badge bg="secondary" className="rounded-pill px-3 py-2">
                      {camas.length} registros
                    </Badge>
                  )}
                </Card.Header>
                <Card.Body className="dh-card-body dh-card-body-flush">
                  <div className="dh-table-wrap">
                    <Table className="dh-table">
                      <thead className="sticky-top">
                        <tr>
                          <th>Zona</th>
                          <th>Módulo</th>
                          <th>Habitación</th>
                          <th>Reserva / Check-in</th>
                          <th>Cama</th>
                          <th>Estado cama</th>
                          <th>DNI trabajador</th>
                          <th>Nombre y apellidos</th>
                          <th className="text-end">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!loadingCamas && camas.length === 0 && (
                          <tr>
                            <td
                              colSpan={9}
                              className="text-center text-muted py-5"
                            >
                              Sin resultados. Ajuste los filtros y pulse
                              &quot;Aplicar filtro&quot;.
                            </td>
                          </tr>
                        )}
                        {camas.map((c) => {
                          const showTrab = esCamaReservadaUOcupada(c.estado);
                          const busy = rowBusyId === c.id;
                          return (
                            <tr key={c.id}>
                              <td>{c.zona}</td>
                              <td>{c.modulo}</td>
                              <td>{c.habitacion}</td>
                              <td>
                                <CeldaFechasReservaCheckin c={c} />
                              </td>
                              <td className="fw-medium">{c.cama}</td>
                              <td>
                                <Badge bg={badgeVariantForEstado(c.estado)}>
                                  {c.estado}
                                </Badge>
                              </td>
                              <td>
                                {showTrab
                                  ? c.trabajador_dni ?? "—"
                                  : "—"}
                              </td>
                              <td>
                                {showTrab
                                  ? c.trabajador_nombre_apellidos ?? "—"
                                  : "—"}
                              </td>
                              <td className="text-end align-middle">
                                <div className="d-flex flex-wrap gap-1 justify-content-end align-items-center">
                                  {esCamaLibre(c.estado) && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        disabled={busy}
                                        onClick={() => abrirModalReserva(c)}
                                      >
                                        <FaUserPlus className="me-1" />
                                        Reservar
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline-success"
                                        disabled={busy}
                                        onClick={() => abrirModalCheckinDirecto(c)}
                                      >
                                        <FaSignInAlt className="me-1" />
                                        Check-in directo
                                      </Button>
                                    </>
                                  )}
                                  {esCamaSoloReservada(c.estado) &&
                                    c.trabajador_dni && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="outline-success"
                                          disabled={busy}
                                          onClick={() =>
                                            setConfirmAccion({
                                              kind: "checkin",
                                              row: c,
                                              title: "Confirmar check-in",
                                              body: (
                                                <>
                                                  Check-in para{" "}
                                                  <strong>
                                                    {c.trabajador_nombre_apellidos ||
                                                      "—"}
                                                  </strong>{" "}
                                                  (DNI {c.trabajador_dni}). Se confirma la
                                                  reserva y se dispara el flujo de ticket del
                                                  proyecto.
                                                </>
                                              ),
                                            })
                                          }
                                        >
                                          <FaSignInAlt className="me-1" />
                                          Check-in
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline-warning"
                                          disabled={busy}
                                          onClick={() =>
                                            setConfirmAccion({
                                              kind: "quitar_reserva",
                                              row: c,
                                              title: "Anular reserva",
                                              body: (
                                                <>
                                                  Se cancelará la reserva de{" "}
                                                  <strong>
                                                    {c.trabajador_nombre_apellidos ||
                                                      "—"}
                                                  </strong>{" "}
                                                  y la cama quedará libre.
                                                </>
                                              ),
                                            })
                                          }
                                        >
                                          <FaTrash className="me-1" />
                                          Quitar reserva
                                        </Button>
                                      </>
                                    )}
                                  {esCamaOcupada(c.estado) && c.trabajador_dni && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        disabled={busy}
                                        onClick={() =>
                                          setConfirmAccion({
                                            kind: "checkout",
                                            row: c,
                                            title: "Check-out",
                                            body: (
                                              <>
                                                Check-out para{" "}
                                                <strong>
                                                  {c.trabajador_nombre_apellidos ||
                                                    "—"}
                                                </strong>{" "}
                                                (DNI {c.trabajador_dni}). La cama quedará
                                                libre.
                                              </>
                                            ),
                                          })
                                        }
                                      >
                                        <FaSignOutAlt className="me-1" />
                                        Check-out
                                      </Button>
                                      <DropdownButton
                                        size="sm"
                                        variant="outline-secondary"
                                        title="Anular…"
                                        disabled={busy}
                                        align="end"
                                      >
                                        <Dropdown.Item
                                          onClick={() =>
                                            setConfirmAccion({
                                              kind: "anular_checkin_solo",
                                              row: c,
                                              title: "Anular solo check-in",
                                              body: (
                                                <>
                                                  La cama vuelve a estado{" "}
                                                  <strong>reservada</strong> (sin fecha de
                                                  check-in). La reserva del trabajador sigue
                                                  activa.
                                                </>
                                              ),
                                            })
                                          }
                                        >
                                          Solo check-in (volver a reservada)
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          onClick={() =>
                                            setConfirmAccion({
                                              kind: "anular_checkin_liberar",
                                              row: c,
                                              title: "Liberar cama",
                                              body: (
                                                <>
                                                  Se cancela la asignación y la cama queda{" "}
                                                  <strong>libre</strong>.
                                                </>
                                              ),
                                            })
                                          }
                                        >
                                          Cancelar asignación y liberar
                                        </Dropdown.Item>
                                      </DropdownButton>
                                    </>
                                  )}
                                  {busy && (
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="ms-1"
                                    />
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        <Modal
          show={!!camaParaReserva}
          onHide={cerrarModalReserva}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {modalLibreTipo === "checkin_directo"
                ? "Check-in directo (cama libre)"
                : "Reservar cama"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {camaParaReserva && (
              <p className="text-muted small mb-3">
                <strong>{camaParaReserva.zona}</strong> › {camaParaReserva.modulo}{" "}
                › Hab. {camaParaReserva.habitacion} › Cama{" "}
                <strong>{camaParaReserva.cama}</strong>
              </p>
            )}
            {modalLibreTipo === "checkin_directo" && (
              <Alert variant="info" className="small py-2">
                Registra ingreso inmediato en esta cama (asignación ocupada). Usa el mismo
                proceso que el check-in manual por Excel; no dispara el flujo de ticket del
                check-in por lector.
              </Alert>
            )}
            {modalLibreTipo === "checkin_directo" && (
              <Form.Group className="mb-3" controlId="fecha-checkin-directo">
                <Form.Label>Fecha de check-in</Form.Label>
                <Form.Control
                  type="date"
                  value={fechaCheckinDirecto}
                  onChange={(e) => setFechaCheckinDirecto(e.target.value)}
                />
              </Form.Group>
            )}
            <Form.Label>Buscar trabajador (DNI o nombre)</Form.Label>
            <InputGroup className="mb-3">
              <Form.Control
                value={busquedaTrabajador}
                onChange={(e) => setBusquedaTrabajador(e.target.value)}
                placeholder="Mínimo 2 caracteres"
                autoFocus
              />
              {buscandoTrabajador && (
                <InputGroup.Text>
                  <Spinner animation="border" size="sm" />
                </InputGroup.Text>
              )}
            </InputGroup>
            <ListGroup
              variant="flush"
              className="border rounded mb-3"
              style={{ maxHeight: 240, overflowY: "auto" }}
            >
              {hitsTrabajador.map((t) => (
                <ListGroup.Item
                  key={t.id}
                  action
                  active={trabajadorElegido?.id === t.id}
                  onClick={() => setTrabajadorElegido(t)}
                >
                  <div className="fw-semibold">{t.nombre_completo}</div>
                  <small className="text-muted">
                    DNI: {t.numero_documento_identidad}
                  </small>
                  {t.tiene_reserva ? (
                    <Badge bg="warning" text="dark" className="ms-2">
                      Ya tiene cama
                    </Badge>
                  ) : null}
                </ListGroup.Item>
              ))}
            </ListGroup>
            {trabajadorElegido && (
              <Alert variant="light" className="mb-0 small border">
                Seleccionado: <strong>{trabajadorElegido.nombre_completo}</strong>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={cerrarModalReserva}>
              Cancelar
            </Button>
            <Button
              className="btn-dh-primary"
              disabled={
                !trabajadorElegido ||
                guardandoReserva ||
                trabajadorElegido?.tiene_reserva ||
                (modalLibreTipo === "checkin_directo" && !fechaCheckinDirecto)
              }
              onClick={() => void confirmarModalLibreCama()}
            >
              {guardandoReserva ? (
                <Spinner animation="border" size="sm" />
              ) : modalLibreTipo === "checkin_directo" ? (
                "Confirmar check-in"
              ) : (
                "Confirmar reserva"
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={!!confirmAccion}
          onHide={() => setConfirmAccion(null)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{confirmAccion?.title || "Confirmar"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{confirmAccion?.body}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setConfirmAccion(null)}>
              Cancelar
            </Button>
            <Button
              className="btn-dh-primary"
              onClick={() => void ejecutarConfirmacion()}
            >
              Confirmar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default EstadoCamasProyecto;
